import { get } from 'lodash-es';

import {
  DefaultReviewersResponse,
  DefaultReviewersUsersResponse,
  AddDefaultReviewers,
  GetAddReviewerOptions,
  GetDefaultReviewers,
  RemoveDefaultReviewer,
} from 'src/components/settings/default-reviewers/types';
import { Project, Repository } from 'src/components/types';
import { getProfileUrl, getName } from 'src/components/user-profile';
import { BuildStatusesMap } from 'src/types';
import dashboardUrls from 'src/urls/dashboard';
import projectUrls from 'src/urls/projects';
import { httpRequest } from 'src/utils/http-request';

export type RepositoryBuildStatus = {
  links: {
    builds: {
      href: string;
    };
  };
  repository: Repository;
  status_counts?: BuildStatusesMap;
  type: 'computed_build';
};

type GetProjectRepoBuildStatuses = (
  repositories: BB.Repository[]
) => Promise<RepositoryBuildStatus[]>;

type ProjectRepositoriesResponse = {
  size: number;
  values: BB.Repository[];
  page: number;
  pagelen: number;
};

export const getProjectRepositories = (
  workspaceSlug: string,
  projectKey: string,
  options?: {
    page?: number;
    pagelen?: number;
    sort?: string;
  }
): Promise<ProjectRepositoriesResponse> =>
  httpRequest<ProjectRepositoriesResponse>().get(
    projectUrls.api.internal.repositories(workspaceSlug, projectKey, options)
  );

export const getProjectLevelDefaultReviewers: GetDefaultReviewers = (
  workspaceSlug,
  projectKey,
  options
) =>
  httpRequest<DefaultReviewersResponse>().get(
    projectUrls.api.v20.defaultReviewers(workspaceSlug, projectKey, options)
  );

export const getProjectAddReviewerOptions: GetAddReviewerOptions = (
  workspaceSlug,
  projectKey,
  options
) =>
  httpRequest<DefaultReviewersUsersResponse>().get(
    projectUrls.api.internal.addDefaultReviewers(
      workspaceSlug,
      projectKey,
      options
    )
  );

export const addProjectLevelDefaultReviewer: AddDefaultReviewers = (
  workspaceSlug,
  projectKey,
  accountIds
) =>
  httpRequest({
    successCustomHandler: () => null, // success returns 204
  }).post(
    projectUrls.api.internal.addDefaultReviewers(workspaceSlug, projectKey),
    {
      account_ids: accountIds,
    }
  );

export const deleteProjectLevelDefaultReviewer: RemoveDefaultReviewer = (
  workspaceSlug,
  projectKey,
  selectedUser
) =>
  httpRequest().delete(
    projectUrls.api.v20.defaultReviewer(workspaceSlug, projectKey, selectedUser)
  );

export const getProjectWorkspaceName: any = (
  project: Project | null | undefined
) => {
  if (!project) {
    return undefined;
  }
  if (project.workspace) {
    return project.workspace.name;
  } else if (project.owner) {
    return getName(project.owner);
  }
  return undefined;
};

export const getProjectWorkspaceUrl: any = (
  project: Project | null | undefined
) => {
  if (!project) {
    return undefined;
  }
  if (project.workspace) {
    return get(project, 'workspace.links.html.href', undefined);
  }
  return getProfileUrl(project.owner);
};

export const getWorkspaceProjectsUrl: any = (
  project: Project | null | undefined
) => {
  if (!project) {
    return undefined;
  }
  if (project.workspace) {
    return `${get(project, 'workspace.links.html.href', undefined)}`.replace(
      /\/?$/,
      '/workspace/projects/'
    );
  }
  return `${getProfileUrl(project.owner)}`.replace(
    /\/?$/,
    '/workspace/projects/'
  );
};

export const getProjectRepositoriesBuildStatuses: GetProjectRepoBuildStatuses =
  (repositories: BB.Repository[]) => {
    return httpRequest<RepositoryBuildStatus[]>({
      headers: {
        'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
    }).post(
      dashboardUrls.api.internal.repositoryBuildStatuses(),
      repositories.map(r => `uuid=${r.uuid}`).join('&')
    );
  };
